<template>
  <div>
    <LoadingWrapper :state="status">
      <h3 class="text-title-medium text-on-surface-variant mt-10 mb-1">
        {{ $t('coaching.surveys.title') }}
      </h3>
      <ul v-if="data.coacheeSurveys.length > 0" class="grid gap-4 md:grid-cols-2">
        <li v-for="survey in data.coacheeSurveys" :key="survey.id">
          <CoacheeSurveyCard :survey="survey" @open="navigateToSurvey" />
        </li>
      </ul>
      <div v-else>
        <span class="text-body-medium text-on-surface-variant">{{
          $t('coaching.surveys.empty.title')
        }}</span>
      </div>

      <div v-if="currentOrganization.modules.includes(ApplicationModule.ASSESSMENT)">
        <h3 class="text-title-medium text-on-surface-variant mt-10 mb-1">
          {{ $t('coaching.assessment.title') }}
        </h3>
        <ul v-if="data.assessments.length > 0" class="flex flex-col gap-0.5">
          <li
            v-for="assessment in data.assessments"
            :key="assessment.id"
            class="bg-surface-container rounded-small first:rounded-t-large last:rounded-b-large"
          >
            <div class="flex flex-row items-center gap-4 p-3">
              <div
                class="bg-primary-container text-on-primary-container flex h-10 w-10 items-center justify-center rounded-full"
              >
                <BaseIcon size="24">{{ assessment.feedbackTopic.icon }}</BaseIcon>
              </div>
              <div class="flex grow flex-col">
                <p
                  v-if="assessment.title"
                  class="text-body-medium text-on-surface-variant truncate"
                >
                  {{ assessment.feedbackTopic.title }}
                </p>
                <h3 class="text-title-medium">
                  {{ assessment.title ?? assessment.feedbackTopic.title }}
                </h3>
                <div class="text-on-surface-variant flex flex-row items-center">
                  <BaseIcon size="18" class="mr-1">today</BaseIcon>
                  <span class="text-body-medium">
                    {{ $t('common.createdAt') }}:
                    <span class="capitalize">
                      {{
                        useDisplayDate(assessment.createdAt, {
                          dayName: 'short',
                        })
                      }}
                    </span>
                  </span>
                </div>
              </div>
              <div
                v-if="assessment.includeSelfFeedback && assessment.selfFeedbackDone != null"
                class="border-outline-variant ml-auto flex flex-row items-center"
                :class="[assessment.peers.length > 0 && 'border-r pr-4']"
              >
                <div
                  class="flex flex-row rounded-full p-1.5 px-3"
                  :class="[
                    assessment.selfFeedbackDone && 'bg-primary-container',
                    !assessment.selfFeedbackDone && 'bg-surface-container-highest',
                  ]"
                >
                  <div
                    v-if="assessment.selfFeedbackDone"
                    class="relative mr-1.5 -ml-1 flex h-4 w-4 grow-0 items-center justify-center"
                  >
                    <BaseIcon size="14" :filled="true" class="text-on-primary z-20">check</BaseIcon>
                    <BaseShape
                      shape="soft-burst"
                      class="text-primary absolute top-1/2 left-1/2 z-10 h-[120%] w-[120%] -translate-x-1/2 -translate-y-1/2 stroke-2"
                    />
                  </div>
                  <span class="text-label-medium" v-if="assessment.selfFeedbackDone"
                    >Selbsteinschätzung erledigt</span
                  >
                  <span class="text-label-medium" v-else>Selbsteinschätzung offen</span>
                </div>
              </div>
              <div v-if="assessment.peers.length > 0" class="flex flex-row items-center gap-2">
                <div class="flex flex-col">
                  <span class="text-label-medium"
                    >{{ assessment.finishedPeerIds.length }}/{{ assessment.peers.length }}
                  </span>
                  <span class="text-label-medium"
                    >{{ $t('common.peers', assessment.peers.length) }}
                  </span>
                </div>
                <div class="flex flex-row -space-x-2">
                  <div
                    v-for="peer in assessment.peers"
                    class="bg-surface-container relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full p-0.5"
                  >
                    <BaseMemberAvatar
                      :member="peer"
                      size="32"
                      :class="[!assessment.finishedPeerIds.includes(peer.id) && 'opacity-70']"
                    />
                    <div
                      v-if="assessment.finishedPeerIds.includes(peer.id)"
                      class="absolute -right-0.5 -bottom-0.5 flex h-5 w-5 items-center justify-center"
                    >
                      <BaseIcon size="14" :filled="true" class="text-on-primary z-20"
                        >check</BaseIcon
                      >
                      <BaseShape
                        shape="soft-burst"
                        class="text-primary absolute right-0 z-10 h-full w-full stroke-2"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
        <div v-else>
          <span class="text-body-medium text-on-surface-variant">{{
            $t('coaching.assessment.empty.title')
          }}</span>
        </div>
      </div>
      <div v-else>
        <h3 class="text-title-medium text-on-surface-variant mt-10 mb-1">
          {{ $t('coaching.feedbackGoals.pageTitle') }}
        </h3>
        <ul v-if="data.feedbackGoals.length > 0" class="grid gap-4 md:grid-cols-2">
          <li v-for="feedbackGoal in data.feedbackGoals" :key="feedbackGoal.id">
            <CoacheeFeedbackGoal :feedbackGoal="feedbackGoal" />
          </li>
        </ul>
        <div v-else>
          <span class="text-body-medium text-on-surface-variant">{{
            $t('coaching.feedbackGoals.empty.title')
          }}</span>
        </div>
      </div>
    </LoadingWrapper>
  </div>
</template>

<script lang="ts" setup>
  import { useCoacheeTodos } from '../composables/useCoacheeTodos'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useRouter } from 'vue-router'
  import CoacheeSurveyCard from '../components/CoacheeSurveyCard.vue'
  import CoacheeFeedbackGoal from '../components/CoacheeFeedbackGoal.vue'
  import { useOrganizationStore } from '@/stores/organization.store'
  import { ApplicationModule } from '@/core/models/organization'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'
  import BaseMemberAvatar from '@/core/components/base/avatar/BaseMemberAvatar.vue'
  import { useDisplayDate } from '@/core/composables/use-display-date'

  const router = useRouter()
  const { data, status } = useCoacheeTodos()

  const navigateToSurvey = (surveyId: string) => {
    router.push({ name: 'coach-survey', params: { id: surveyId } })
  }
  const { currentOrganization } = useOrganizationStore()
</script>
